import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '@/context/AuthContext';

// Pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import EmployeesPage from '@/pages/EmployeesPage';
import EmployeeDetailPage from '@/pages/EmployeeDetailPage';
import DevicesPage from '@/pages/DevicesPage';
import DeviceDetailPage from '@/pages/DeviceDetailPage';
import TimesheetsPage from './pages/TimesheetsPage';
import PenaltiesPage from '@/pages/PenaltiesPage';
import ShiftsPage from '@/pages/ShiftsPage';
import ReportsPage from '@/pages/ReportsPage';
import BranchesPage from '@/pages/BranchesPage';
import RolesPage from '@/pages/RolesPage';
import DepartmentsPage from '@/pages/DepartmentsPage';
import UsersPage from '@/pages/UsersPage';
import OrganizationsPage from '@/pages/OrganizationsPage';
import ShiftAssignmentsPage from '@/pages/ShiftAssignmentsPage';
import EmployeeDayOffsPage from '@/pages/EmployeeDayOffsPage';
import HolidaysPage from '@/pages/HolidaysPage';
import PenaltyRulesPage from '@/pages/PenaltyRulesPage';
import PenaltyPresetsPage from '@/pages/PenaltyPresetsPage';
import AttendanceEventsPage from '@/pages/AttendanceEventsPage';
import AuditLogsPage from '@/pages/AuditLogsPage';
import TelegramLinksPage from '@/pages/TelegramLinksPage';
// Payroll Pages
import PayrollRunsPage from '@/pages/PayrollRunsPage';
import PayrollRunDetailPage from '@/pages/PayrollRunDetailPage';
import PayrollProfilesPage from '@/pages/PayrollProfilesPage';
import LeaveRequestsPage from '@/pages/LeaveRequestsPage';
import SalaryComponentsPage from '@/pages/SalaryComponentsPage';
import ProfilePage from '@/pages/ProfilePage';
import OperationsCenterPage from './pages/OperationsCenterPage';
import ShiftChangeRequestsPage from '@/pages/ShiftChangeRequestsPage';

// Components
import MainLayout from '@/components/layouts/MainLayout';
import { LoadingSpinner } from '@/components/common';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Permission-protected route wrapper
const PermissionRoute: React.FC<{
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  role?: string;
}> = ({ children, permission, permissions, role }) => {
  const { can, hasRole } = useAuth();

  // Check role requirement
  if (role && !hasRole(role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check permission requirement
  if (permission && !can(permission)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (permissions && !permissions.some((requiredPermission) => can(requiredPermission))) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Employees - manage_employees permission */}
        <Route path="/employees" element={<PermissionRoute permission="manage_employees"><EmployeesPage /></PermissionRoute>} />
        <Route path="/employees/:id" element={<PermissionRoute permission="manage_employees"><EmployeeDetailPage /></PermissionRoute>} />

        {/* Devices - manage_devices permission */}
        <Route path="/devices" element={<PermissionRoute permission="manage_devices"><DevicesPage /></PermissionRoute>} />
        <Route path="/devices/:id" element={<PermissionRoute permission="manage_devices"><DeviceDetailPage /></PermissionRoute>} />

        {/* Attendance - view_attendance permission */}
        <Route path="/timesheets" element={<PermissionRoute permission="view_attendance"><TimesheetsPage /></PermissionRoute>} />
        <Route path="/penalties" element={<PermissionRoute permissions={['manage_penalties', 'approve_penalties']}><PenaltiesPage /></PermissionRoute>} />
        <Route path="/attendance-events" element={<PermissionRoute permission="view_attendance"><AttendanceEventsPage /></PermissionRoute>} />
        <Route path="/operations" element={<PermissionRoute permission="view_attendance"><OperationsCenterPage /></PermissionRoute>} />

        {/* Shifts - manage_shifts permission */}
        <Route path="/shifts" element={<PermissionRoute permission="manage_shifts"><ShiftsPage /></PermissionRoute>} />
        <Route path="/shift-assignments" element={<PermissionRoute permission="manage_shifts"><ShiftAssignmentsPage /></PermissionRoute>} />
        <Route path="/employee-day-offs" element={<PermissionRoute permission="manage_shifts"><EmployeeDayOffsPage /></PermissionRoute>} />
        <Route path="/shift-change-requests" element={<PermissionRoute permission="manage_shifts"><ShiftChangeRequestsPage /></PermissionRoute>} />

        {/* Holidays - manage_holidays permission */}
        <Route path="/holidays" element={<PermissionRoute permission="manage_holidays"><HolidaysPage /></PermissionRoute>} />

        {/* Penalty Rules - manage_penalties permission */}
        <Route path="/penalty-presets" element={<PermissionRoute permission="manage_penalties"><PenaltyPresetsPage /></PermissionRoute>} />
        <Route path="/penalty-rules" element={<PermissionRoute permission="manage_penalties"><PenaltyRulesPage /></PermissionRoute>} />

        {/* Payroll Module - view_payroll permission */}
        <Route path="/payroll/runs" element={<PermissionRoute permission="view_payroll"><PayrollRunsPage /></PermissionRoute>} />
        <Route path="/payroll/runs/:id" element={<PermissionRoute permission="view_payroll"><PayrollRunDetailPage /></PermissionRoute>} />
        <Route path="/payroll/profiles" element={<PermissionRoute permission="manage_payroll"><PayrollProfilesPage /></PermissionRoute>} />
        <Route path="/payroll/leaves" element={<PermissionRoute permissions={['manage_leaves', 'approve_leaves']}><LeaveRequestsPage /></PermissionRoute>} />
        <Route path="/payroll/salary-components" element={<PermissionRoute permission="manage_payroll"><SalaryComponentsPage /></PermissionRoute>} />

        {/* Reports - attendance or payroll visibility */}
        <Route path="/reports" element={<PermissionRoute permissions={['view_attendance', 'view_payroll']}><ReportsPage /></PermissionRoute>} />
        <Route path="/profile" element={<ProfilePage />} />

        {/* Admin */}
        <Route path="/organizations" element={<PermissionRoute role="super_admin"><OrganizationsPage /></PermissionRoute>} />
        <Route path="/branches" element={<PermissionRoute permission="manage_branches"><BranchesPage /></PermissionRoute>} />
        <Route path="/departments" element={<PermissionRoute permission="manage_departments"><DepartmentsPage /></PermissionRoute>} />
        <Route path="/roles" element={<PermissionRoute permission="manage_roles"><RolesPage /></PermissionRoute>} />
        <Route path="/users" element={<PermissionRoute permission="manage_users"><UsersPage /></PermissionRoute>} />
        <Route path="/admin/telegram-links" element={<PermissionRoute permission="manage_telegram"><TelegramLinksPage /></PermissionRoute>} />
        <Route path="/audit-logs" element={<PermissionRoute role="super_admin"><AuditLogsPage /></PermissionRoute>} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export default function App() {
  return (
    <Router>
      <AppRoutes />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
    </Router>
  );
}
